import React, { useState, useRef, useMemo } from 'react';
import { Navbar } from '@/components/Navbar';
import { Hero } from '@/components/Hero';
import { ImageModal } from '@/components/ImageModal';
import { Footer } from '@/components/Footer';
import { TopContactStrip } from '@/components/TopContactStrip';
import { TopBar } from '@/components/TopBar';
import { useGallery } from '@/hooks/useGallery';
import { BRANDS, BrandId, GalleryImage } from '@/lib/storage';
import { Eye, Edit3, Trash2, GripVertical, RotateCcw, Download } from 'lucide-react';

interface ImageCardProps {
  image: GalleryImage;
  index: number;
  isAdmin: boolean;
  onSelect: (image: GalleryImage | null) => void;
  onEdit: (image: GalleryImage) => void;
  onDelete: (id: string) => void;
  brandColor: string;
}

const ImageCard: React.FC<ImageCardProps> = ({
  image,
  index,
  isAdmin,
  onSelect,
  onEdit,
  onDelete,
  brandColor,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [imageError, setImageError] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const thumbHeight = isAdmin ? 338 : 324;

  const handleDragStart = (e: React.DragEvent) => {
    if (!isAdmin) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', image.id);
    cardRef.current?.classList.add('opacity-50');
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    cardRef.current?.classList.remove('opacity-50');
    setIsDragging(false);
    setDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!isAdmin) return;
    e.preventDefault();
    const draggedId = e.dataTransfer.getData('text/plain');
    if (draggedId && draggedId !== image.id) {
      window.dispatchEvent(new CustomEvent('reorder-images', {
        detail: { draggedId, targetId: image.id }
      }));
    }
    setDragOver(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-action]')) return;
    onSelect(image);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(image);
    }
  };

  return (
    <article
      ref={cardRef}
      draggable={isAdmin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View ${image.alt || image.brandId} motorcycle`}
      className={`group relative card-base cursor-pointer overflow-hidden transition-all duration-300 ${dragOver ? 'ring-2 ring-gold scale-[1.02] z-10' : ''} ${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="relative overflow-hidden bg-dark-bg flex items-center justify-center" style={{ maxHeight: `${thumbHeight}px` }}>
        {!imageError && (
          <img
            src={image.url}
            alt={image.alt || `${image.brandId} motorcycle`}
            loading="lazy"
            onError={() => setImageError(true)}
            className="max-w-full object-contain mx-auto transition-transform duration-500 group-hover:scale-105"
            style={{ maxHeight: `${thumbHeight}px` }}
          />
        )}
        {imageError && (
          <div className="w-full flex items-center justify-center bg-dark-card" style={{ maxHeight: `${thumbHeight}px` }}>
            <span className="text-4xl">🏍️</span>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="absolute top-3 left-3 right-3 flex items-start justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${brandColor === 'gold' ? 'bg-gold/90 text-dark-bg' : `bg-${brandColor}-500/90 text-white`}`}>
            {image.brandId.toUpperCase()}
          </span>
          <div className="flex gap-1.5">
            <button
              data-action
              onClick={(e) => { e.stopPropagation(); onSelect(image); }}
              className="h-9 w-9 rounded-xl bg-black/60 backdrop-blur text-white flex items-center justify-center hover:bg-white/10 transition-colors"
              aria-label="View details"
            >
              <Eye className="h-4 w-4" />
            </button>
            {isAdmin && (
              <>
                <button
                  data-action
                  onClick={(e) => { e.stopPropagation(); onEdit(image); }}
                  className="h-9 w-9 rounded-xl bg-black/60 backdrop-blur text-white flex items-center justify-center hover:bg-white/10 transition-colors"
                  aria-label="Edit image"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
                <button
                  data-action
                  onClick={(e) => { e.stopPropagation(); onDelete(image.id); }}
                  className="h-9 w-9 rounded-xl bg-red-500/20 text-red-400 flex items-center justify-center hover:bg-red-500/40 transition-colors"
                  aria-label="Delete image"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className="absolute top-3 left-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              data-action
              onClick={(e) => e.stopPropagation()}
              className="h-8 w-8 rounded-xl bg-black/60 backdrop-blur text-neutral-400 flex items-center justify-center hover:text-white transition-colors cursor-grab active:cursor-grabbing"
              aria-label="Drag to reorder"
            >
              <GripVertical className="h-5 w-5" />
            </button>
          </div>
        )}

        {isAdmin && (
          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="text-[10px] font-mono text-neutral-400 bg-black/60 backdrop-blur px-2 py-1 rounded">
              #{index + 1}
            </span>
            <div className="flex gap-1.5">
              <button
                data-action
                onClick={(e) => { e.stopPropagation(); onEdit({ ...image, rotation: (image.rotation || 0) + 90 }); }}
                className="h-8 w-8 rounded-xl bg-black/60 backdrop-blur text-white flex items-center justify-center hover:bg-white/10 transition-colors"
                aria-label="Rotate"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <a
                data-action
                href={image.url}
                download={image.alt || `jb-motorcycles-${image.brandId}-${index + 1}`}
                className="h-8 w-8 rounded-xl bg-black/60 backdrop-blur text-white flex items-center justify-center hover:bg-white/10 transition-colors"
                aria-label="Download"
              >
                <Download className="h-4 w-4" />
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="font-semibold text-white truncate">{image.alt || 'Untitled'}</h3>
          {image.isFeatured && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/10 text-gold text-[10px] font-bold uppercase tracking-wider">
              <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
              Featured
            </span>
          )}
        </div>
        <p className="text-xs text-neutral-500 font-mono">
          {new Date(image.updatedAt).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
        </p>
      </div>
    </article>
  );
};

const Home: React.FC = () => {
  const {
    images,
    activeBrand,
    setActiveBrand,
    selectedImage,
    setSelectedImage,
    handleDeleteImage,
    handleEditImage,
    handleNavigate,
    toggleAdmin,
    isAdmin,
  } = useGallery();

  const handleBrandChange = (brandId: BrandId) => {
    setActiveBrand(brandId);
  };

  const handleAdminClick = () => {
    toggleAdmin(); // This redirects to /admin.php
  };

  const brand = BRANDS.find(b => b.id === activeBrand);
  const brandImages = useMemo(() =>
    images.filter(img => img.brandId === activeBrand).sort((a, b) => a.sortOrder - b.sortOrder),
    [images, activeBrand]
  );

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <TopContactStrip />
      <Navbar
        activeBrand={activeBrand}
        onBrandChange={handleBrandChange}
        onToggleAdmin={handleAdminClick}
        isAdmin={false}
      />
      <TopBar />

      <main className="flex-1">
        <Hero activeBrand={activeBrand} brandName={brand?.label || ''} />

        <div id="gallery" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pb-20">
          <div className="flex flex-col sm:flex-row sm:items-baseline justify-between border-b border-dark-border pb-5 mb-8 gap-2">
            <div>
              <h2 className="text-xl sm:text-2xl font-extrabold uppercase tracking-wide">
                {brand?.label} Collection
              </h2>
              <p className="text-neutral-500 text-xs mt-0.5 font-mono uppercase">
                Showing {brandImages.length} motorcycle{brandImages.length !== 1 ? 's' : ''}
              </p>
            </div>
            {isAdmin && (
              <a
                href="/admin.php"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gold/10 text-gold border border-gold/30 px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-widest hover:bg-gold/20 transition-colors flex items-center gap-2"
              >
                <span>+</span> Manage Images
              </a>
            )}
          </div>

          {brandImages.length === 0 ? (
            <div className="bg-dark-card/50 border border-dark-border rounded-3xl p-12 md:p-20 text-center">
              <div className="max-w-md mx-auto">
                <div className={`h-16 w-16 rounded-2xl bg-${brand?.color || 'gold'}-500/10 flex items-center justify-center mx-auto mb-6`}>
                  <span className="text-2xl">🏍️</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">No images in {brand?.label} yet</h3>
                <p className="text-neutral-500 text-sm mb-6">
                  {isAdmin
                    ? 'Use the "Manage Images" button to upload via admin panel.'
                    : 'Check back soon for new arrivals in this category.'}
                </p>
                {isAdmin && (
                  <a
                    href="/admin.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gold text-dark-bg font-bold text-xs uppercase tracking-widest px-6 py-3 rounded-xl hover:bg-gold-hover transition-colors inline-block"
                  >
                    Manage Images
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              role="list"
              aria-label={`${brand?.label} motorcycles`}
            >
              {brandImages.map((image, index) => (
                <ImageCard
                  key={image.id}
                  image={image}
                  index={index}
                  isAdmin={isAdmin}
                  onSelect={setSelectedImage}
                  onEdit={handleEditImage}
                  onDelete={handleDeleteImage}
                  brandColor={brand?.color === 'gold' ? 'gold' : brand?.color || 'gold'}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />

      <ImageModal
        image={selectedImage}
        images={images}
        isAdmin={isAdmin}
        onClose={() => setSelectedImage(null)}
        onEdit={handleEditImage}
        onDelete={handleDeleteImage}
        onNavigate={handleNavigate}
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Home />
  );
};

export default App;